// AnalisisIA — bloque que llama a Claude (vía edge function analizar-reporte)
// y muestra insights contextuales para un reporte.
// Uso: <AnalisisIA reportType="mrr_churn" data={{ filas, totales }} />

import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'

const TIPO_STYLE = {
  positivo: { bg: '#d1fae5', border: '#a7f3d0', color: '#065f46', icon: '✅' },
  neutral:  { bg: '#dbeafe', border: '#93c5fd', color: '#1e40af', icon: 'ℹ️' },
  alerta:   { bg: '#fef3c7', border: '#fcd34d', color: '#92400e', icon: '⚠️' },
  critico:  { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b', icon: '🚨' },
}

export default function AnalisisIA({ reportType, data, autoRun = false, titulo = 'Análisis IA del reporte' }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [hasRun, setHasRun] = useState(false)

  const correr = useCallback(async () => {
    setLoading(true)
    setError(null)
    setHasRun(true)
    try {
      const { data: resp, error: e } = await supabase.functions.invoke('analizar-reporte', {
        body: { reportType, data },
      })
      if (e) throw e
      if (resp?.error) throw new Error(resp.error)
      setResultado(resp)
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setLoading(false)
    }
  }, [reportType, data])

  // Auto-run si autoRun=true (no usado por defecto, para evitar tokens innecesarios)
  // useEffect(() => { if (autoRun && !hasRun) correr() }, [autoRun, hasRun, correr])

  return (
    <div className="ia-card">
      <div className="ia-head">
        <div className="ia-title-row">
          <div className="ia-bot-badge">
            <span className="ia-bot-dot"></span>
            <span>🤖 Claude</span>
          </div>
          <div>
            <div className="ia-titulo">{titulo}</div>
            <div className="ia-sub">
              {resultado?.resumen
                ? resultado.resumen
                : 'Analizá los datos con IA para detectar patrones, alertas y oportunidades.'}
            </div>
          </div>
        </div>
        <button
          className="ia-btn"
          onClick={correr}
          disabled={loading}
        >
          {loading ? 'Analizando…' : hasRun ? '🔄 Re-analizar' : '🤖 Analizar con IA'}
        </button>
      </div>

      {error && (
        <div className="ia-error">
          <strong>Error:</strong> {error}
          {error.includes('ANTHROPIC_API_KEY') && (
            <div style={{ marginTop: 6, fontSize: 11 }}>
              Configurá el secret en Supabase: <code>supabase secrets set ANTHROPIC_API_KEY=sk-ant-...</code>
            </div>
          )}
        </div>
      )}

      {resultado?.insights && resultado.insights.length > 0 && (
        <div className="ia-insights">
          {resultado.insights.map((ins, i) => {
            const st = TIPO_STYLE[ins.tipo] || TIPO_STYLE.neutral
            return (
              <div key={i} className="ia-insight" style={{ background: st.bg, borderColor: st.border }}>
                <div className="ia-insight-icon">{ins.icono || st.icon}</div>
                <div style={{ flex: 1 }}>
                  <div className="ia-insight-titulo" style={{ color: st.color }}>{ins.titulo}</div>
                  <div className="ia-insight-texto">{ins.texto}</div>
                </div>
              </div>
            )
          })}
          {resultado.usage && (
            <div className="ia-meta">
              {resultado.model} · {resultado.usage.input_tokens} in / {resultado.usage.output_tokens} out tokens
            </div>
          )}
        </div>
      )}

      {!hasRun && !loading && (
        <div className="ia-empty">
          Hacé click en <strong>“Analizar con IA”</strong> para que Claude lea los datos del reporte y te dé 3-4 insights accionables. Cuesta unos centavos por análisis.
        </div>
      )}

      <style>{`
        .ia-card {
          background: linear-gradient(135deg, #faf7ff 0%, #fef3ff 100%);
          border: 1px solid #d8b4fe;
          border-radius: 14px;
          padding: 18px;
          margin: 24px 0;
          position: relative;
          overflow: hidden;
        }
        .ia-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 3px;
          background: linear-gradient(90deg, #c084fc 0%, #f0abfc 50%, #67e8f9 100%);
        }
        .ia-head {
          display: flex; gap: 16px;
          align-items: flex-start; justify-content: space-between;
          flex-wrap: wrap;
        }
        .ia-title-row {
          display: flex; gap: 12px; align-items: flex-start; flex: 1; min-width: 0;
        }
        .ia-bot-badge {
          display: inline-flex; align-items: center; gap: 6px;
          background: #1a1a1a; color: #fff;
          padding: 5px 10px; border-radius: 999px;
          font-size: 11px; font-weight: 700; letter-spacing: .03em;
          flex-shrink: 0;
        }
        .ia-bot-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #00A8B5; box-shadow: 0 0 6px #00A8B5;
          animation: ia-pulse 2s ease-in-out infinite;
        }
        @keyframes ia-pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }
        .ia-titulo { font-weight: 800; color: #581c87; font-size: 15px; }
        .ia-sub { font-size: 12px; color: #7c3aed; margin-top: 2px; line-height: 1.5; }
        .ia-btn {
          padding: 9px 16px;
          background: #1a1a1a; color: #fff;
          border: 0; border-radius: 999px;
          font-size: 13px; font-weight: 700;
          cursor: pointer;
          transition: all .15s;
          white-space: nowrap;
        }
        .ia-btn:hover:not(:disabled) {
          background: #4c1d95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(76, 29, 149, .3);
        }
        .ia-btn:disabled { opacity: .6; cursor: wait; }
        .ia-empty {
          margin-top: 12px;
          font-size: 12px; color: #7c3aed; opacity: .8;
          line-height: 1.6;
        }
        .ia-error {
          margin-top: 12px;
          padding: 10px 12px;
          background: #fee2e2; border: 1px solid #fca5a5;
          border-radius: 8px;
          color: #991b1b; font-size: 12px;
        }
        .ia-insights {
          margin-top: 14px;
          display: flex; flex-direction: column; gap: 8px;
        }
        .ia-insight {
          display: flex; gap: 12px;
          padding: 12px 14px;
          border-radius: 10px;
          border: 1px solid;
        }
        .ia-insight-icon { font-size: 20px; line-height: 1; flex-shrink: 0; padding-top: 2px; }
        .ia-insight-titulo { font-weight: 700; font-size: 13px; margin-bottom: 3px; }
        .ia-insight-texto { font-size: 13px; line-height: 1.5; color: #374151; }
        .ia-meta {
          font-size: 10px; color: #94a3b8;
          text-align: right; margin-top: 6px;
        }
      `}</style>
    </div>
  )
}
